// Modèle Excel du journal de paie — alternative au PDF Cegid pour l'outil « Contrôle ».
// Onglet « Journal » : une ligne par salarié (mêmes colonnes que le journal Cegid).
// Onglet « Entete »  : société, établissement, période.

import * as XLSX from 'xlsx';
import { frDate } from './journalPaieControle';

export const EXCEL_COLUMNS = [
  ['matricule', 'Matricule'],
  ['nom', 'Nom Prénom'],
  ['date_entree', "Date d'entrée"],
  ['date_sortie', 'Date de sortie'],
  ['heures', 'Heures'],
  ['brut', 'Brut'],
  ['brut_abattu', 'Brut abattu'],
  ['ret_sal', 'Ret. Sal.'],
  ['ret_pat', 'Ret. Pat.'],
  ['assujet', 'Assujet.'],
  ['net_imposable', 'Net imposable'],
  ['divers_plus', 'Divers plus'],
  ['divers_moins', 'Divers moins'],
  ['net_a_payer', 'Net à payer'],
];

const DATE_COLS = ['date_entree', 'date_sortie'];
const TEXT_COLS = ['matricule', 'nom'];

const ENTETE_FIELDS = [
  ['societe', 'Société'],
  ['etablissement', 'Établissement'],
  ['periode_debut', 'Période début'],
  ['periode_fin', 'Période fin'],
];

// Lignes d'exemple (cohérentes avec les formules contrôlées : CNSS 4,48 % plafonnée, AMO 2,26 %,
// complémentaire 2 % / 3,06 %, frais pro 35 % ou 25 %, net = brut abattu − ret. sal. + divers ± )
const EXEMPLES = [
  {
    matricule: '00001', nom: 'EXEMPLE SALARIE UN', date_entree: '2017-07-18', date_sortie: null, heures: 191,
    brut: 7498.70, brut_abattu: 8498.70, ret_sal: 588.24, ret_pat: 1676.36, assujet: 0,
    net_imposable: 5035.78, divers_plus: 0.02, divers_moins: 224.48, net_a_payer: 7686,
  },
  {
    matricule: '00009', nom: 'EXEMPLE SALARIE DEUX', date_entree: '2018-05-01', date_sortie: null, heures: 191,
    brut: 3450.90, brut_abattu: 3450.90, ret_sal: 301.61, ret_pat: 833.39, assujet: 0,
    net_imposable: 1941.47, divers_plus: 0.66, divers_moins: 0.95, net_a_payer: 3149,
  },
  {
    matricule: '01006', nom: 'EXEMPLE SALARIE SORTI', date_entree: '2017-05-10', date_sortie: '2025-09-01', heures: 191,
    brut: 3500, brut_abattu: 3500, ret_sal: 305.90, ret_pat: 845.25, assujet: 0,
    net_imposable: 1969.10, divers_plus: 0.47, divers_moins: 300.57, net_a_payer: 2894,
  },
];

const toSheetRow = (l) => Object.fromEntries(EXCEL_COLUMNS.map(([k, label]) => [
  label,
  DATE_COLS.includes(k) ? frDate(l[k]) : (l[k] ?? ''),
]));

function buildWorkbook(entete, lignes, withNotes) {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(lignes.map(toSheetRow), { header: EXCEL_COLUMNS.map(([, l]) => l) });
  ws['!cols'] = EXCEL_COLUMNS.map(([k]) => ({ wch: k === 'nom' ? 32 : 14 }));
  XLSX.utils.book_append_sheet(wb, ws, 'Journal');

  const wsE = XLSX.utils.aoa_to_sheet([
    ['Champ', 'Valeur'],
    ...ENTETE_FIELDS.map(([k, label]) => [label, k.startsWith('periode') ? frDate(entete[k]) : (entete[k] || '')]),
  ]);
  wsE['!cols'] = [{ wch: 18 }, { wch: 28 }];
  XLSX.utils.book_append_sheet(wb, wsE, 'Entete');

  if (withNotes) {
    const wsN = XLSX.utils.aoa_to_sheet([
      ['Mode d\'emploi'],
      ['1. Onglet « Entete » : renseigner la société, l\'établissement (ou « Tous ») et la période (jj/mm/aaaa). La période début est obligatoire.'],
      ['2. Onglet « Journal » : une ligne par salarié, mêmes colonnes que le Journal de Paie Cegid. Remplacer les lignes d\'exemple.'],
      ['3. Dates au format jj/mm/aaaa, montants en DH (virgule ou point acceptés). Date de sortie et Heures peuvent rester vides.'],
      ['4. Ne pas renommer les onglets ni les en-têtes de colonnes.'],
      ['Exemples : les 2 premières lignes sont conformes ; la 3e (salarié sorti avant la période mais payé) illustre une alerte du contrôle.'],
      ['Note : un import Excel n\'a pas de « TOTAL GENERAL » imprimé — le rapprochement des totaux n\'est disponible que pour les PDF Cegid.'],
    ]);
    wsN['!cols'] = [{ wch: 120 }];
    XLSX.utils.book_append_sheet(wb, wsN, 'Notes');
  }
  return wb;
}

const today = () => new Date().toISOString().slice(0, 10);

export function downloadTemplate() {
  const d = new Date();
  const debut = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  const fin = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const finIso = `${fin.getFullYear()}-${String(fin.getMonth() + 1).padStart(2, '0')}-${String(fin.getDate()).padStart(2, '0')}`;
  const wb = buildWorkbook({ societe: 'ELITE PARCS', etablissement: 'Tous', periode_debut: debut, periode_fin: finIso }, EXEMPLES, true);
  XLSX.writeFile(wb, `modele_journal_paie_${today()}.xlsx`);
}

// Exporte un journal (PDF importé ou enregistré) au format du modèle — modifiable puis réimportable
export function exportAsTemplate(journal) {
  const wb = buildWorkbook(journal, journal.lignes || [], false);
  const soc = String(journal.societe || 'societe').replace(/[^a-z0-9]+/gi, '_');
  XLSX.writeFile(wb, `journal_paie_${soc}_${(journal.periode_debut || '').slice(0, 7)}.xlsx`);
}

// ── Lecture ──────────────────────────────────────────────────────────────────
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');

const pad = (n) => String(n).padStart(2, '0');

function toIso(v) {
  if (v == null || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? undefined : `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  if (typeof v === 'number') {
    const p = XLSX.SSF.parse_date_code(v);
    return p ? `${p.y}-${pad(p.m)}-${pad(p.d)}` : undefined;
  }
  const s = String(v).trim();
  let m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
  if (m) return `${m[3]}-${pad(m[2])}-${pad(m[1])}`;
  m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return undefined; // invalide
}

function toNum(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v;
  const n = Number(String(v).replace(/[\s  ]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

const findSheet = (wb, name) => wb.SheetNames.find((n) => norm(n) === norm(name));

// Lit le modèle → { societe, etablissement, periode_debut, periode_fin, fichier_nom, lignes }
export async function readTemplate(file) {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const journalSheet = findSheet(wb, 'Journal') || wb.SheetNames[0];
  const enteteSheet = findSheet(wb, 'Entete');
  if (!enteteSheet) throw new Error('Onglet « Entete » introuvable — utilisez le modèle Excel');

  const entete = {};
  XLSX.utils.sheet_to_json(wb.Sheets[enteteSheet], { header: 1, defval: '' }).forEach(([champ, valeur]) => {
    const f = ENTETE_FIELDS.find(([, label]) => norm(label) === norm(champ));
    if (f) entete[f[0]] = valeur;
  });
  const periode_debut = toIso(entete.periode_debut);
  if (!periode_debut) throw new Error('« Période début » manquante ou invalide dans l\'onglet « Entete » (format jj/mm/aaaa)');
  const periode_fin = toIso(entete.periode_fin);
  if (periode_fin === undefined) throw new Error('« Période fin » invalide dans l\'onglet « Entete »');

  const rows = XLSX.utils.sheet_to_json(wb.Sheets[journalSheet], { defval: '' });
  if (!rows.length) throw new Error('Onglet « Journal » vide');

  // Correspondance en-têtes du fichier → colonnes attendues (accents / casse / ponctuation ignorés)
  const fileCols = Object.keys(rows[0]);
  const colMap = {};
  const missing = [];
  for (const [k, label] of EXCEL_COLUMNS) {
    const found = fileCols.find((c) => norm(c) === norm(label));
    if (found) colMap[k] = found;
    else if (!['date_sortie', 'heures', 'assujet', 'divers_plus'].includes(k)) missing.push(label);
  }
  if (missing.length) throw new Error(`Colonnes manquantes dans l'onglet « Journal » : ${missing.join(', ')}`);

  const lignes = [];
  rows.forEach((row, i) => {
    const line = {};
    const ligneNo = i + 2;
    if (EXCEL_COLUMNS.every(([k]) => !colMap[k] || row[colMap[k]] === '')) return; // ligne vide
    for (const [k, label] of EXCEL_COLUMNS) {
      const raw = colMap[k] ? row[colMap[k]] : '';
      if (TEXT_COLS.includes(k)) line[k] = String(raw ?? '').trim() || null;
      else if (DATE_COLS.includes(k)) {
        const d = toIso(raw);
        if (d === undefined) throw new Error(`Ligne ${ligneNo} : ${label} invalide (« ${raw} »)`);
        line[k] = d;
      } else {
        const n = toNum(raw);
        if (n === undefined) throw new Error(`Ligne ${ligneNo} : ${label} invalide (« ${raw} »)`);
        line[k] = k === 'heures' ? n : (n ?? 0);
      }
    }
    if (!line.matricule) throw new Error(`Ligne ${ligneNo} : matricule manquant`);
    lignes.push(line);
  });
  if (!lignes.length) throw new Error('Aucune ligne salarié dans l\'onglet « Journal »');

  return {
    societe: String(entete.societe || '').trim() || null,
    etablissement: String(entete.etablissement || '').trim() || 'Tous',
    periode_debut,
    periode_fin: periode_fin || null,
    fichier_nom: file.name,
    lignes,
  };
}

export const isExcelFile = (file) => /\.(xlsx|xls)$/i.test(file?.name || '');
